<!-- AnalyticsChart.vue -->
<template>
  <div class="flex min-h-[564px] relative">
    <!-- Loading 狀態 -->
    <div 
      v-if="loading" 
      class="absolute inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 rounded-lg"
    >
      <div class="text-white text-center">
        <img src=".././../images/icons/loading.png" class="w-6 h-6 ml-2 icon loading-icon"/>
      </div>
    </div>

    <!-- 圖表容器 -->
    <canvas 
      ref="chartCanvas" 
      width="100%" 
      height="100%"
      :class="{ 'opacity-50': loading }"
    />
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch, nextTick, inject } from 'vue'
import { Chart, registerables } from 'chart.js'

export default {
  name: 'ProfileAnalyticsChart',
  setup() {
    // 從父組件注入 analytics 實例
    const analytics = inject('analytics')
    
    if (!analytics) {
      console.error('ProfileAnalyticsChart: analytics not provided')
      return {}
    }
    
    const {
      chartData,
      chartParams,
      loading,
      getDateRange,
      getTotalList
    } = analytics

    // DOM 引用
    const chartCanvas = ref(null)
    let chart = null
    let isMounted = false

    // 圖表顏色映射
    const colorMapping = {
      'post_views': '#DC49D6',
      'revenue': '#2BDEE9',
      'like_counts': '#FFCB68',
      'download_counts': '#29CB00',
      'follow_counts': 'rgba(153, 102, 255, 1)'
    }

    // 圖表配置
    const chartConfig = {
      datasets: {
        fill: false,
        tension: 0.1
      },
      options: {
        maintainAspectRatio: false,
        responsive: true,
        scales: {
          y: {
            grid: {
              display: true,
              drawBorder: true,
              borderColor: '#3E434A',
              color: '#3E434A',
              borderDash: [],  // 實線
              borderWidth: 1
            },
            ticks: {
              beginAtZero: true,
              color: 'white',
              borderColor: 'white',
              callback: function(value, index, values) {
                // 避免小數
                if (Math.floor(value) === value) {
                  return value;
                }
              }
            }
          },
          x: {
            grid: {
              display: true,
              color: '#3E434A',
              borderColor: '#3E434A',
              borderDash: [6, 3],  // 虛線模式：6px實線，3px空白
              borderWidth: 1
            },
            ticks: {
              color: 'white',
              autoSkip: true,
              maxTicksLimit: 5,
              maxRotation: 0,
              minRotation: 0
            }
          }
        },
        plugins: {
          legend: {
            display: true,
            position: "top",
            align: "end",
            labels: {
              usePointStyle: true,
              pointStyle: "circle",
              color: '#99A0A8',
              boxWidth: 5,
              boxHeight: 5,
            },
          },
          tooltip: {
            titleAlign: "center",
            usePointStyle: true,
            padding: 10,
            bodySpacing: 8,
            boxWidth: 5,
            boxHeight: 5,
            boxPadding: 5,
            callbacks: {
              label: function(context) {
                let label = context.dataset.label || '';
                let value = context.parsed.y !== null ? context.parsed.y : '';
                if (label === 'Revenues') {
                  return `${label}: $${value}`;
                } else {
                  return `${label}: ${value}`;
                }
              }
            }
          }
        },
        interaction: {
          mode: 'index'
        }
      }
    }

    // 翻譯數據源標籤
    const translateLabel = (source) => {
      const labelMap = {
        'post_views': 'Views',
        'like_counts': 'Likes',
        'download_counts': 'Downloads',
        'revenue': 'Revenues',
        'follow_counts': 'Follows'
      }
      return labelMap[source] || source
    }

    // 初始化圖表
    const initializeChart = async () => {
      // 檢查組件是否已掛載
      if (!isMounted) {
        console.warn('Chart initialization skipped: component not mounted yet')
        return
      }
      
      // 等待 DOM 更新
      await nextTick()
      
      // 如果 canvas 不存在，等待一段時間後重試
      if (!chartCanvas.value) {
        console.warn('Chart canvas not found, retrying...')
        
        // 重試機制：等待 100ms 後再次嘗試
        setTimeout(async () => {
          if (!isMounted) {
            console.warn('Chart retry skipped: component unmounted')
            return
          }
          
          await nextTick()
          if (!chartCanvas.value) {
            console.error('Chart canvas still not found after retry')
            return
          }
          
          // 重試成功，繼續初始化
          initializeChartInstance()
        }, 100)
        return
      }
      
      // 直接初始化
      initializeChartInstance()
    }
    
    // 實際初始化圖表實例
    const initializeChartInstance = () => {
      if (!isMounted) {
        console.warn('Chart instance initialization skipped: component not mounted')
        return
      }
      
      if (!chartCanvas.value) {
        console.error('Cannot initialize chart: canvas not found')
        return
      }
      
      Chart.register(...registerables)
      
      chart = new Chart(chartCanvas.value, {
        type: 'line',
        data: {
          labels: [],
          datasets: []
        },
        options: chartConfig.options
      })

    }

    // 更新圖表數據
    const updateChart = () => {
      if (!chart || !chartData.value) return
      
      const dateRange = getDateRange(chartParams.start, chartParams.end)
      
      // 判斷是否為 all_data 模式
      if (chartParams.source === 'all_data') {
        updateAllChart(dateRange)
      } else {
        updateSingleChart(dateRange)
      }
    }

    // 更新所有數據源的圖表
    const updateAllChart = (dateRange) => {
      chart.data.labels = dateRange
      chart.data.datasets = []
      
      // 定義所有可能的數據源
      const allSources = ['post_views', 'revenue', 'like_counts', 'download_counts']
      
      allSources.forEach(source => {
        const data = chartData.value[source]
        const type = source === 'revenue' ? 'total_price' : 'total_count'
        
        let datasets
        if (data && Object.keys(data).length > 0) {
          // 有數據時使用實際數據
          datasets = getTotalList(dateRange, data, type)
        } else {
          // 沒有數據時創建全是 0 的數據
          datasets = new Array(dateRange.length).fill(0)
        }
        
        chart.data.datasets.push({
          label: translateLabel(source),
          data: datasets,
          borderColor: colorMapping[source],
          backgroundColor: colorMapping[source],
          ...chartConfig.datasets
        })
      })
      
      chart.update('active')
    }

    // 更新單一數據源的圖表
    const updateSingleChart = (dateRange) => {
      const source = chartParams.source
      const data = chartData.value[source]
      
      const type = source === 'revenue' ? 'total_price' : 'total_count'
      let datasets
      
      if (data && Object.keys(data).length > 0) {
        // 有數據時使用實際數據
        datasets = getTotalList(dateRange, data, type)
      } else {
        // 沒有數據時創建全是 0 的數據
        datasets = new Array(dateRange.length).fill(0)
      }
      
      chart.data.labels = dateRange
      chart.data.datasets = [{
        label: translateLabel(source),
        data: datasets,
        borderColor: colorMapping[source],
        backgroundColor: colorMapping[source],
        ...chartConfig.datasets
      }]
      
      chart.update('active')
    }

    // 調整圖表尺寸
    const resizeChart = () => {
      if (chart) {
        chart.resize()
      }
    }

    // 強制刷新圖表配置
    const refreshChart = () => {
      if (chart) {
        // 銷毀並重新創建圖表
        chart.destroy()
        chart = null
        initializeChartInstance()
        updateChart()
      }
    }

    // 監聽器
    watch(() => chartData.value, () => {
      if (chart && !loading.value) {
        updateChart()
      }
    }, { deep: true })

    watch(() => chartParams, () => {
      if (chart && !loading.value) {
        updateChart()
      }
    }, { deep: true })

    watch(loading, (newLoading) => {
      if (!newLoading && chart) {
        // Loading 結束後稍微延遲更新圖表
        setTimeout(() => {
          updateChart()
        }, 100)
      }
    })

    // 生命週期
    onMounted(() => {
      isMounted = true
      initializeChart().then(() => {
        // 圖表初始化後，強制更新一次以確保配置生效
        if (chart) {
          chart.update('none')  // 立即更新，不使用動畫
        }
        
        // 如果有數據就更新
        if (chartData.value && Object.keys(chartData.value).length > 0) {
          updateChart()
        }
      })
      
      // 列印支援
      window.addEventListener('afterprint', resizeChart)
      // 窗口大小調整支援
      window.addEventListener('resize', resizeChart)
    })

    onUnmounted(() => {
      isMounted = false
      
      // 清理圖表實例
      if (chart) {
        chart.destroy()
        chart = null
      }
      
      // 移除事件監聽器
      window.removeEventListener('afterprint', resizeChart)
      window.removeEventListener('resize', resizeChart)
    })

    return {
      chartCanvas,
      loading
    }
  }
}
</script>
